import React, { useEffect, useState, useCallback } from "react";
import {
  Card, Button, Modal, Form, InputNumber, Radio, message,
  Table, Tag, Empty, Spin, Alert, Row, Col, Tooltip, Input,
  Select, DatePicker, Badge, Dropdown
} from "antd";
import {
  WalletOutlined, DollarOutlined, ClockCircleOutlined,
  CheckCircleOutlined, CloseCircleOutlined, SyncOutlined,
  PlusOutlined, BankOutlined, ArrowDownOutlined,
  HistoryOutlined, SearchOutlined, FilterOutlined,
  ReloadOutlined, EyeOutlined, FileTextOutlined,
  ExclamationCircleOutlined, RiseOutlined, InfoCircleOutlined,
  CalendarOutlined, MoreOutlined, DownloadOutlined
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { getDeveloperDashboard, submitDeveloperWithdrawal } from "../../../../Api/CoreApi";
import "./DeveloperWithdrawal.css";

const { RangePicker } = DatePicker;
const { Option } = Select;

function DeveloperWithdrawal() {
  const navigate = useNavigate();
  const [form] = Form.useForm();


  // States
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailDrawer, setDetailDrawer] = useState({ open: false, record: null });
  const [dashboardData, setDashboardData] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState("upi");
  const [refreshing, setRefreshing] = useState(false);

  // Filter States
  const [searchText, setSearchText] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [dateRange, setDateRange] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchDashboard = useCallback(async (showRefresh = false) => {
    try {
      if (showRefresh) setRefreshing(true);
      else setLoading(true);
      const data = await getDeveloperDashboard();

      console.log("🎯 Dashboard Data:", data);
      console.log("💰 Earnings:", data?.earnings);
      console.log("💰 ALL KEYS:", data ? Object.keys(data) : []);
      console.log("💰 EARNINGS DETAIL:", JSON.stringify(data?.earnings, null, 2));
      console.log("📋 Withdrawals:", data?.recent_withdrawals || data?.withdrawals);
      console.log("🏦 Bank Info:", data?.bank_details);

      if (data) {
        const withdrawals = data.recent_withdrawals || data.withdrawals || [];
        const transactions = data.transactions || data.recent_transactions || [];

        // ✅ Start with whatever backend gives
        let earnings = { ...(data.earnings || {}) };

        // ✅ STEP 1: Map alternative field names
        const fieldMappings = {
          total_earnings: ['total_earnings', 'total_commission', 'total_amount', 'commission', 'earnings', 'dev_amount', 'total'],
          available_balance: ['available_balance', 'balance', 'available', 'current_balance', 'remaining', 'dev_balance'],
          total_withdrawn: ['total_withdrawn', 'withdrawn', 'total_withdrawal'],
          pending_withdrawal: ['pending_withdrawal', 'pending', 'pending_amount'],
        };

        Object.entries(fieldMappings).forEach(([targetField, possibleFields]) => {
          if (!earnings[targetField] && earnings[targetField] !== 0) {
            for (const field of possibleFields) {
              if (earnings[field] !== undefined && earnings[field] !== null && parseFloat(earnings[field]) >= 0) {
                earnings[targetField] = parseFloat(earnings[field]);
                console.log(`✅ Mapped ${field} → ${targetField}: ${earnings[targetField]}`);
                break;
              }
            }
          }
        });

        // ✅ STEP 2: If still no earnings, calculate from transactions
        if ((!earnings.total_earnings || earnings.total_earnings === 0) && transactions.length > 0) {
          console.log("📊 Calculating earnings from transactions...");

          earnings.total_earnings = transactions
            .filter(t => t.type === 'credit' && (t.status === 'success' || t.status === 'completed' || t.status === 'approved'))
            .reduce((sum, t) => sum + (parseFloat(t.amount) || 0), 0);

          earnings.total_withdrawn = withdrawals
            .filter(w => w.status === 'completed' || w.status === 'approved' || w.status === 'processing')
            .reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);

          earnings.pending_withdrawal = withdrawals
            .filter(w => w.status === 'pending')
            .reduce((sum, w) => sum + (parseFloat(w.amount) || 0), 0);

          earnings.available_balance = Math.max(0,
            earnings.total_earnings - (earnings.total_withdrawn || 0) - (earnings.pending_withdrawal || 0)
          );
        }

        // ✅ STEP 3: Ensure all fields have at least 0
        earnings.total_earnings = parseFloat(earnings.total_earnings) || 0;
        earnings.available_balance = parseFloat(earnings.available_balance) || 0;
        earnings.total_withdrawn = parseFloat(earnings.total_withdrawn) || 0;
        earnings.pending_withdrawal = parseFloat(earnings.pending_withdrawal) || 0;

        // ✅ STEP 4: If available_balance is still 0 but total_earnings > 0, calculate it
        if (earnings.available_balance === 0 && earnings.total_earnings > 0) {
          earnings.available_balance = Math.max(0,
            earnings.total_earnings - earnings.total_withdrawn - earnings.pending_withdrawal
          );
        }

    

        setDashboardData({
          ...data,
          earnings: earnings,
        });
      }
    } catch (error) {
      console.error("Dashboard error:", error);
      message.error("Failed to load dashboard");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Auto refresh every 30s
  useEffect(() => {
    const interval = setInterval(() => fetchDashboard(true), 30000);
    return () => clearInterval(interval);
  }, [fetchDashboard]);

  // Filtered Data
  const getFilteredData = useCallback(() => {
    let data = dashboardData?.recent_withdrawals || [];

    // Search
    if (searchText.trim()) {
      const s = searchText.toLowerCase();
      data = data.filter((r) =>
        `WDL${String(r.id).padStart(6, "0")}`.toLowerCase().includes(s) ||
        parseFloat(r.amount || 0).toString().includes(s) ||
        r.status?.toLowerCase().includes(s)
      );
    }

    // Status filter
    if (statusFilter !== "all") {
      data = data.filter((r) => r.status === statusFilter);
    }

    // Method filter
    if (methodFilter !== "all") {
      if (methodFilter === "upi") data = data.filter((r) => r.upi_id);
      else data = data.filter((r) => !r.upi_id);
    }

    // Date range
    if (dateRange && dateRange[0] && dateRange[1]) {
      const start = dateRange[0].startOf("day").valueOf();
      const end = dateRange[1].endOf("day").valueOf();
      data = data.filter((r) => {
        const d = new Date(r.date).getTime();
        return d >= start && d <= end;
      });
    }

    return data;
  }, [dashboardData, searchText, statusFilter, methodFilter, dateRange]);

  const filteredData = getFilteredData();

  // Stats from filtered data
  const getFilteredStats = useCallback(() => {
    const data = filteredData;
    return {
      total: data.length,
      totalAmount: data.reduce((sum, r) => sum + parseFloat(r.amount || 0), 0),
      pending: data.filter((r) => r.status === "pending").length,
      completed: data.filter((r) => r.status === "completed").length,
      rejected: data.filter((r) => r.status === "rejected").length,
      processing: data.filter((r) => r.status === "processing" || r.status === "approved").length,
    };
  }, [filteredData]);

  const filteredStats = getFilteredStats();

  // Clear filters
  const clearFilters = () => {
    setSearchText("");
    setStatusFilter("all");
    setMethodFilter("all");
    setDateRange(null);
    setCurrentPage(1);
  };

  const hasActiveFilters = searchText || statusFilter !== "all" || methodFilter !== "all" || dateRange;

  // Withdraw click
  const handleWithdrawClick = () => {
    if (!dashboardData?.bank_details?.has_bank_details && !dashboardData?.bank_details?.has_upi) {
      Modal.warning({
        title: "⚠️ Add Payment Details First",
        content: "Please add your bank account or UPI ID before requesting withdrawal.",
        okText: "Add Now",
        okButtonProps: {
          style: { background: "linear-gradient(135deg, #ff0055, #ff4d88)", border: "none" }
        },
        onOk: () => navigate("/developer/bank-details"),
      });
      return;
    }
    if (dashboardData?.earnings?.available_balance <= 0) {
      message.warning("No balance available for withdrawal");
      return;
    }
    setModalOpen(true);
  };

  // Submit withdrawal
  const handleSubmit = async (values) => {
    try {
      setSubmitting(true);
      const response = await submitDeveloperWithdrawal({
        amount: values.amount,
        payment_method: paymentMethod,
      });
      if (response?.success) {
        Modal.success({
          title: "🎉 Request Submitted!",
          content: `Withdrawal request of ₹${values.amount.toLocaleString("en-IN")} sent to admin for approval.`,
          okButtonProps: {
            style: { background: "linear-gradient(135deg, #ff0055, #ff4d88)", border: "none" }
          },
        });
        setModalOpen(false);
        form.resetFields();
        fetchDashboard(true);
      }
    } catch (error) {
      if (error.message?.includes("bank details") || error.message?.includes("UPI")) {
        Modal.warning({
          title: "Bank Details Required",
          content: error.message,
          okText: "Add Details",
          okButtonProps: {
            style: { background: "linear-gradient(135deg, #ff0055, #ff4d88)", border: "none" }
          },
          onOk: () => navigate("/developer/bank-details"),
        });
      } else {
        message.error(error.message || "Submission failed");
      }
    } finally {
      setSubmitting(false);
    }
  };

  // Status config
  const getStatusConfig = (status) => {
    const cfg = {
      pending: { className: "pending", icon: <ClockCircleOutlined />, label: "Pending", color: "#faad14" },
      approved: { className: "approved", icon: <CheckCircleOutlined />, label: "Approved", color: "#1890ff" },
      processing: { className: "processing", icon: <SyncOutlined spin />, label: "Processing", color: "#ff0055" },
      completed: { className: "completed", icon: <CheckCircleOutlined />, label: "Completed", color: "#52c41a" },
      rejected: { className: "rejected", icon: <CloseCircleOutlined />, label: "Rejected", color: "#f5222d" },
    };
    return cfg[status] || cfg.pending;
  };

  // Table columns
  const columns = [
    {
      title: "#",
      key: "sno",
      width: 50,
      render: (_, __, i) => (
        <span className="dev-wd-sno">{(currentPage - 1) * pageSize + i + 1}</span>
      ),
    },
    {
      title: "Reference",
      key: "ref",
      width: 130,
      render: (_, r) => (
        <span className="dev-wd-ref">WDL{String(r.id).padStart(6, "0")}</span>
      ),
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
      sorter: (a, b) => parseFloat(a.amount) - parseFloat(b.amount),
      render: (a) => (
        <span className="dev-wd-amount">
          ₹{parseFloat(a || 0).toLocaleString("en-IN")}
        </span>
      ),
    },
    {
      title: "Method",
      key: "method",
      width: 100,
      render: (_, r) => (
        <Tag className={`dev-wd-method-tag ${r.upi_id ? "dev-wd-method-upi" : "dev-wd-method-bank"}`}>
          {r.upi_id ? "💳 UPI" : "🏦 Bank"}
        </Tag>
      ),
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      width: 120,
      sorter: (a, b) => new Date(a.date) - new Date(b.date),
      render: (d) => (
        <span className="dev-wd-date">
          <CalendarOutlined style={{ marginRight: 4 }} />
          {new Date(d).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 130,
      render: (s) => {
        const cfg = getStatusConfig(s);
        return (
          <Tag className={`dev-wd-status dev-wd-status-${cfg.className}`}>
            {cfg.icon} {cfg.label}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 60,
      fixed: "right",
      render: (_, record) => (
        <Tooltip title="View Details">
          <Button
            type="text"
            size="small"
            className="dev-wd-action-btn"
            icon={<EyeOutlined />}
            onClick={() => setDetailDrawer({ open: true, record })}
          />
        </Tooltip>
      ),
    },
  ];

  // Loading state
  if (loading) {
    return (
      <div className="dev-wd-page">
        <div className="dev-wd-loading">
          <Spin size="large" />
          <p>Loading your dashboard...</p>
        </div>
      </div>
    );
  }

  const earnings = dashboardData?.earnings || {};
  const stats = dashboardData?.stats || {};
  const bankInfo = dashboardData?.bank_details || {};

  return (
    <div className="dev-wd-page">

      {/* ===== HEADER ===== */}
      <div className="dev-wd-header">
        <div className="dev-wd-header-icon">
          <WalletOutlined />
        </div>
        <div className="dev-wd-header-content">
          <h1>My Withdrawals</h1>
          <p>Manage your commission earnings and withdrawal requests</p>
        </div>
        <div className="dev-wd-header-actions">
          <Tooltip title="Refresh Data">
            <Button
              icon={<ReloadOutlined spin={refreshing} />}
              className="dev-wd-refresh-btn"
              onClick={() => fetchDashboard(true)}
            />
          </Tooltip>
          <Button
            type="primary"
            size="large"
            icon={<PlusOutlined />}
            className="dev-wd-request-btn"
            onClick={handleWithdrawClick}
          >
            New Withdrawal
          </Button>
        </div>
      </div>

      {/* ===== STAT CARDS ===== */}
      <Row gutter={[12, 12]} className="dev-wd-stats-row">
        {[
          {
            key: "earnings",
            label: "Total Earnings",
            value: earnings.total_earnings,
            icon: <DollarOutlined />,
            gradient: "linear-gradient(135deg, #52c41a, #73d13d)",
            shadow: "rgba(82, 196, 26, 0.25)",
          },
          {
            key: "available",
            label: "Available Balance",
            value: earnings.available_balance,
            icon: <WalletOutlined />,
            gradient: "linear-gradient(135deg, #ff0055, #ff4d88)",
            shadow: "rgba(255, 0, 85, 0.25)",
          },
          {
            key: "withdrawn",
            label: "Total Withdrawn",
            value: earnings.total_withdrawn,
            icon: <ArrowDownOutlined />,
            gradient: "linear-gradient(135deg, #3b82f6, #60a5fa)",
            shadow: "rgba(59, 130, 246, 0.25)",
          },
          {
            key: "pending",
            label: "Pending Amount",
            value: earnings.pending_withdrawal,
            icon: <ClockCircleOutlined />,
            gradient: "linear-gradient(135deg, #faad14, #ffc53d)",
            shadow: "rgba(250, 173, 20, 0.25)",
          },
        ].map((stat) => (
          <Col xs={24} sm={12} md={6} key={stat.key}>
            <div className={`dev-wd-stat-card-new dev-wd-stat-${stat.key}`}>
              <div
                className="dev-wd-stat-icon-new"
                style={{ background: stat.gradient, boxShadow: `0 4px 12px ${stat.shadow}` }}
              >
                {stat.icon}
              </div>
              <div className="dev-wd-stat-content-new">
                <span className="dev-wd-stat-label-new">{stat.label}</span>
                <span className="dev-wd-stat-value-new">
                  ₹{(stat.value || 0).toLocaleString("en-IN")}
                </span>
              </div>
            </div>
          </Col>
        ))}
      </Row>

      {/* ===== ALERT - No Bank Details ===== */}
      {(!bankInfo.has_bank_details && !bankInfo.has_upi) && (
        <Alert
          message="⚠️ Add Payment Details"
          description="You need to add your bank account or UPI ID before requesting withdrawals."
          type="warning"
          showIcon
          icon={<ExclamationCircleOutlined />}
          action={
            <Button
              type="primary"
              size="small"
              className="dev-wd-alert-btn"
              onClick={() => navigate("/developer/bank-details")}
            >
              Add Now
            </Button>
          }
          className="dev-wd-alert"
        />
      )}

      {/* ===== FILTERS ===== */}
      <div className="dev-wd-filters-card">
        <div className="dev-wd-filters-row">
          <div className="dev-wd-filter-group dev-wd-filter-search">
            <label>Search</label>
            <Input
              prefix={<SearchOutlined />}
              placeholder="Search by ref, amount..."
              value={searchText}
              onChange={(e) => { setSearchText(e.target.value); setCurrentPage(1); }}
              allowClear
            />
          </div>

          <div className="dev-wd-filter-group dev-wd-filter-status">
            <label>Status</label>
            <Select
              value={statusFilter}
              onChange={(v) => { setStatusFilter(v); setCurrentPage(1); }}
              style={{ width: "100%" }}
            >
              <Option value="all">All Status</Option>
              <Option value="pending">Pending</Option>
              <Option value="approved">Approved</Option>
              <Option value="processing">Processing</Option>
              <Option value="completed">Completed</Option>
              <Option value="rejected">Rejected</Option>
            </Select>
          </div>

          <div className="dev-wd-filter-group dev-wd-filter-method">
            <label>Method</label>
            <Select
              value={methodFilter}
              onChange={(v) => { setMethodFilter(v); setCurrentPage(1); }}
              style={{ width: "100%" }}
            >
              <Option value="all">All Methods</Option>
              <Option value="upi">UPI</Option>
              <Option value="bank">Bank</Option>
            </Select>
          </div>

          <div className="dev-wd-filter-group dev-wd-filter-date">
            <label>Date Range</label>
            <RangePicker
              value={dateRange}
              onChange={(d) => { setDateRange(d); setCurrentPage(1); }}
              style={{ width: "100%" }}
              format="DD/MM/YYYY"
            />
          </div>

          {hasActiveFilters && (
            <div className="dev-wd-filter-group dev-wd-filter-clear">
              <label>&nbsp;</label>
              <Button
                className="dev-wd-clear-btn"
                onClick={clearFilters}
                icon={<CloseCircleOutlined />}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ===== HISTORY TABLE ===== */}
      <div className="dev-wd-history-card-new">
        <div className="dev-wd-history-header">
          <h3>
            <HistoryOutlined /> Withdrawal History
          </h3>
          <div className="dev-wd-history-stats">
            <span className="dev-wd-mini-stat">
              <b>{filteredStats.total}</b> <span>Total</span>
            </span>
            <span className="dev-wd-mini-stat dev-wd-mini-pending">
              <ClockCircleOutlined /> <b>{filteredStats.pending}</b> <span>Pending</span>
            </span>
            <span className="dev-wd-mini-stat dev-wd-mini-completed">
              <CheckCircleOutlined /> <b>{filteredStats.completed}</b> <span>Done</span>
            </span>
            {filteredStats.rejected > 0 && (
              <span className="dev-wd-mini-stat dev-wd-mini-rejected">
                <CloseCircleOutlined /> <b>{filteredStats.rejected}</b> <span>Rejected</span>
              </span>
            )}
          </div>
        </div>

        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredData}
          size="small"
          className="dev-wd-table"
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: filteredData.length,
            onChange: (p, ps) => { setCurrentPage(p); setPageSize(ps); },
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total}`,
            pageSizeOptions: ["5", "10", "20", "50"],
          }}
          scroll={{ x: 700 }}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={
                  hasActiveFilters
                    ? "No withdrawals match your filters"
                    : "No withdrawal requests yet"
                }
              >
                {!hasActiveFilters && (
                  <Button
                    type="primary"
                    className="dev-wd-empty-btn"
                    icon={<PlusOutlined />}
                    onClick={handleWithdrawClick}
                  >
                    Create First Withdrawal
                  </Button>
                )}
              </Empty>
            ),
          }}
        />
      </div>

      {/* ===== DETAIL MODAL ===== */}
      <Modal
        title={
          <div className="dev-wd-detail-modal-title">
            <FileTextOutlined /> Withdrawal Details
          </div>
        }
        open={detailDrawer.open}
        onCancel={() => setDetailDrawer({ open: false, record: null })}
        footer={
          <Button
            type="primary"
            className="dev-wd-submit-btn"
            onClick={() => setDetailDrawer({ open: false, record: null })}
          >
            Close
          </Button>
        }
        width={480}
      >
        {detailDrawer.record && (() => {
          const r = detailDrawer.record;
          const cfg = getStatusConfig(r.status);
          return (
            <div className="dev-wd-detail-content">
              <div className="dev-wd-detail-amount-section">
                <div className="dev-wd-detail-amount-circle">
                  <ArrowDownOutlined />
                </div>
                <div className="dev-wd-detail-amount-value">
                  ₹{parseFloat(r.amount || 0).toLocaleString("en-IN")}
                </div>
                <Tag className={`dev-wd-status dev-wd-status-${cfg.className}`}>
                  {cfg.icon} {cfg.label}
                </Tag>
              </div>

              <div className="dev-wd-detail-info-grid">
                <div className="dev-wd-detail-info-item">
                  <span className="dev-wd-detail-info-label">Reference ID</span>
                  <span className="dev-wd-detail-info-value dev-wd-ref">
                    WDL{String(r.id).padStart(6, "0")}
                  </span>
                </div>
                <div className="dev-wd-detail-info-item">
                  <span className="dev-wd-detail-info-label">Payment Method</span>
                  <span className="dev-wd-detail-info-value">
                    {r.upi_id ? `💳 UPI (${r.upi_id})` : "🏦 Bank Transfer"}
                  </span>
                </div>
                <div className="dev-wd-detail-info-item">
                  <span className="dev-wd-detail-info-label">Request Date</span>
                  <span className="dev-wd-detail-info-value">
                    {new Date(r.date).toLocaleDateString("en-IN", {
                      day: "2-digit", month: "long", year: "numeric",
                    })}
                  </span>
                </div>
                {r.processed_date && (
                  <div className="dev-wd-detail-info-item">
                    <span className="dev-wd-detail-info-label">Processed Date</span>
                    <span className="dev-wd-detail-info-value">
                      {new Date(r.processed_date).toLocaleDateString("en-IN", {
                        day: "2-digit", month: "long", year: "numeric",
                      })}
                    </span>
                  </div>
                )}
                {r.remark && (
                  <div className="dev-wd-detail-info-item dev-wd-detail-info-full">
                    <span className="dev-wd-detail-info-label">Remark</span>
                    <span className="dev-wd-detail-info-value">{r.remark}</span>
                  </div>
                )}
              </div>
            </div>
          );
        })()}
      </Modal>

      {/* ===== WITHDRAWAL MODAL ===== */}
      <Modal
        title={
          <div className="dev-wd-modal-title">
            <WalletOutlined /> New Withdrawal Request
          </div>
        }
        open={modalOpen}
        onCancel={() => { setModalOpen(false); form.resetFields(); }}
        footer={null}
        width={500}
        destroyOnClose
      >
        <Alert
          message={
            <span>
              Available Balance: <strong style={{ color: "#ff0055", fontSize: 16 }}>
                ₹{(earnings.available_balance || 0).toLocaleString("en-IN")}
              </strong>
            </span>
          }
          type="info"
          showIcon
          icon={<WalletOutlined style={{ color: "#ff0055" }} />}
          className="dev-wd-modal-alert"
        />

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          className="dev-wd-modal-form"
        >
          <Form.Item label="Payment Method">
            <Radio.Group
              value={paymentMethod}
              onChange={(e) => setPaymentMethod(e.target.value)}
              className="dev-wd-method-group"
            >
              <Radio.Button value="upi" disabled={!bankInfo.has_upi}>
                💳 UPI {!bankInfo.has_upi && "(Not Set)"}
              </Radio.Button>
              <Radio.Button value="bank" disabled={!bankInfo.has_bank_details}>
                🏦 Bank {!bankInfo.has_bank_details && "(Not Set)"}
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          {paymentMethod === "upi" && bankInfo.upi_id && (
            <div className="dev-wd-method-info">
              <InfoCircleOutlined style={{ color: "#ff0055", marginRight: 6 }} />
              <span>UPI ID:</span> <strong>{bankInfo.upi_id}</strong>
            </div>
          )}
          {paymentMethod === "bank" && bankInfo.bank_account && (
            <div className="dev-wd-method-info">
              <div><InfoCircleOutlined style={{ color: "#ff0055", marginRight: 6 }} />
                Bank: <strong>{bankInfo.bank_account}</strong></div>
              <div style={{ marginLeft: 22 }}>IFSC: <strong>{bankInfo.ifsc_code}</strong></div>
            </div>
          )}

          <Form.Item
            label="Withdrawal Amount (₹)"
            name="amount"
            rules={[
              { required: true, message: "Amount is required" },
              { type: "number", min: 100, message: "Minimum ₹100" },
              {
                type: "number",
                max: earnings.available_balance,
                message: `Maximum ₹${(earnings.available_balance || 0).toLocaleString("en-IN")}`,
              },
            ]}
          >
            <InputNumber
              min={100}
              max={earnings.available_balance}
              size="large"
              placeholder="Enter amount"
              style={{ width: "100%" }}
              formatter={(v) => `₹ ${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
              parser={(v) => v.replace(/₹\s?|(,*)/g, "")}
            />
          </Form.Item>

          {/* Quick Amount Buttons */}
          <div className="dev-wd-quick-amounts">
            {[500, 1000, 2000, 5000].map((amt) => (
              <Button
                key={amt}
                size="small"
                className="dev-wd-quick-btn"
                disabled={amt > (earnings.available_balance || 0)}
                onClick={() => form.setFieldsValue({ amount: amt })}
              >
                ₹{amt.toLocaleString("en-IN")}
              </Button>
            ))}
            {earnings.available_balance > 0 && (
              <Button
                size="small"
                className="dev-wd-quick-btn dev-wd-quick-max"
                onClick={() => form.setFieldsValue({ amount: earnings.available_balance })}
              >
                Max
              </Button>
            )}
          </div>

          <div className="dev-wd-modal-actions">
            <Button
              className="dev-wd-cancel-modal-btn"
              onClick={() => { setModalOpen(false); form.resetFields(); }}
            >
              Cancel
            </Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={submitting}
              className="dev-wd-submit-btn"
              icon={<ArrowDownOutlined />}
            >
              {submitting ? "Submitting..." : "Submit Request"}
            </Button>
          </div>
        </Form>
      </Modal>
    </div>
  );
}

export default DeveloperWithdrawal;